import React, { useState } from 'react';
import { testApi } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

interface AITestButtonProps {
  onListingsGenerated?: () => void;
  className?: string;
}

export const AITestButton: React.FC<AITestButtonProps> = ({ 
  onListingsGenerated, 
  className = '' 
}) => {
  const { user } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [count, setCount] = useState(10);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [lastResult, setLastResult] = useState<string>('');

  // Only show for admin users (check both role and accountRole)
  if (!user || (user.role !== 'admin' && user.accountRole !== 'admin' && user.accountRole !== 'owner')) {
    return null;
  }

  const categories = [
    'Timber', 'Metal', 'Fixtures', 'Doors', 'Windows', 
    'Roofing', 'Flooring', 'Insulation', 'Electrical', 'Plumbing', 'Other'
  ];

  const handleGenerateListings = async () => {
    setIsGenerating(true);
    setLastResult('');
    
    try {
      const response = await testApi.generateListings({
        count,
        categories: selectedCategories.length > 0 ? selectedCategories : undefined
      });

      if (response.data.success) {
        setLastResult(`✅ Generated ${response.data.data?.count || 0} test listings successfully!`);
        setShowOptions(false);
        if (onListingsGenerated) {
          onListingsGenerated();
        }
      } else {
        setLastResult('❌ Failed to generate listings');
      }
    } catch (error: any) {
      console.error('Failed to generate listings:', error);
      const message = error.response?.data?.error?.message || 'Failed to generate listings';
      setLastResult(`❌ Error: ${message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteTestData = async () => {
    if (!window.confirm('Are you sure you want to delete all test/mock data? This cannot be undone.')) {
      return;
    }

    setIsGenerating(true);
    setLastResult('🔄 Deleting test data... This may take a moment.');
    setShowOptions(false); // Close options panel immediately
    
    try {
      const response = await testApi.deleteAllMockData();
      
      if (response.data.success) {
        const result = response.data.data;
        setLastResult(
          `🗑️ Deleted: ${result?.listings || 0} listings, ${result?.items || 0} items, ` +
          `${result?.projects || 0} projects, ${result?.orders || 0} orders, ` +
          `${result?.testCollections || 0} test collections`
        );
        if (onListingsGenerated) {
          onListingsGenerated();
        }
      } else {
        setLastResult('❌ Failed to delete test data');
      }
    } catch (error: any) {
      console.error('Failed to delete test data:', error);
      const message = error.response?.data?.error?.message || 'Failed to delete test data';
      setLastResult(`❌ Error: ${message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  return (
    <div className={`relative ${className}`}>
      {/* Main Button */}
      <button
        onClick={() => setShowOptions(!showOptions)}
        disabled={isGenerating}
        className={`btn-primary p-2 ${
          isGenerating ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        title="AI Test"
      >
        {isGenerating ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        )}
      </button>

      {/* Options Panel */}
      {showOptions && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-slate-800 
                        rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 
                        p-4 z-50">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 dark:text-white">
                AI Test Options
              </h3>
              <button
                onClick={() => setShowOptions(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Count Input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Number of Listings
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={count}
                onChange={(e) => setCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 
                           rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Categories */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Categories (optional)
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                {categories.map((category) => (
                  <label key={category} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category)}
                      onChange={() => toggleCategory(category)}
                      className="rounded border-slate-300 dark:border-slate-600 
                                 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      {category}
                    </span>
                  </label>
                ))}
              </div>
              {selectedCategories.length > 0 && (
                <button
                  onClick={() => setSelectedCategories([])}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1"
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-2">
              <button
                onClick={handleGenerateListings}
                disabled={isGenerating}
                className="flex-1 px-3 py-2 bg-green-600 text-white rounded-md 
                           hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed
                           transition-colors duration-200 text-sm font-medium"
              >
                Generate Listings
              </button>
              <button
                onClick={handleDeleteTestData}
                disabled={isGenerating}
                className="flex-1 px-3 py-2 bg-red-600 text-white rounded-md 
                           hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed
                           transition-colors duration-200 text-sm font-medium"
              >
                Delete Test Data
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Message */}
      {lastResult && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-slate-800 
                        rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 
                        p-3 z-40">
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {lastResult}
          </p>
          <button
            onClick={() => setLastResult('')}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
};
